import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, HeartPulse, Stethoscope, Sparkles } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { useMasters } from "../form/engine";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const PRACTITIONERS = ["doctor", "nutritionist"];
const inputCls = "w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60";

const ChipRow = ({ options, values, onToggle, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {options.map((o) => (
      <button key={o} type="button" data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => onToggle(o)}
        className={`rounded-full px-3 py-1.5 text-[10px] font-bold transition-colors ${values.includes(o) ? "bg-[#22C55E] text-[#09090B]" : "border border-white/10 text-slate-400 hover:text-white"}`}>
        {o}
      </button>
    ))}
  </div>
);

// Simple CSS-bar trend chart — weight + sugar over the logged period
const TrendChart = ({ logs, metric, color, label, unit }) => {
  const rows = logs.filter((l) => l[metric] != null);
  if (rows.length < 2) return null;
  const vals = rows.map((l) => l[metric]);
  const min = Math.min(...vals);
  const span = Math.max(...vals) - min || 1;
  const delta = rows[rows.length - 1][metric] - rows[0][metric];
  return (
    <div data-testid={`trend-${metric}`}>
      <div className="flex h-16 items-end gap-1">
        {rows.map((l, i) => (
          <div key={i} title={`${l.date}: ${l[metric]}${unit}`}
            className={`flex-1 rounded-t ${color}`}
            style={{ height: `${18 + ((l[metric] - min) / span) * 82}%` }} />
        ))}
      </div>
      <p className="mt-1 font-jbmono text-[9px] text-slate-500">
        {label} · {rows[0].date} → {rows[rows.length - 1].date} · <span className={delta <= 0 ? "text-[#22C55E]" : "text-[#F97316]"}>{delta > 0 ? "+" : ""}{Math.round(delta * 10) / 10}{unit}</span>
      </p>
    </div>
  );
};

// HealthBite — health profile, FUNZI AI meal plans (allergy-filtered), doctor/
// nutritionist consultations with diet notes.
export const HealthBitePanel = () => {
  const { user } = useAuth();
  const lang = useLang();
  const masters = useMasters();
  const isPractitioner = PRACTITIONERS.includes(user.role) || user.role === "admin";
  const [tab, setTab] = useState("plan");
  const [health, setHealth] = useState(undefined);
  const [recs, setRecs] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [kind, setKind] = useState("");
  const [mine, setMine] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [bookingFor, setBookingFor] = useState(null);
  const [bForm, setBForm] = useState({ date: "", slot: "9 AM", reason: "" });
  const [noteFor, setNoteFor] = useState("");
  const [note, setNote] = useState("");
  const [pForm, setPForm] = useState({ age: "", conditions: [], allergies: [], goal: "", lifestyle: "" });
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logForm, setLogForm] = useState({ weight_kg: "", sugar_mgdl: "", meal_note: "" });
  const [progressFor, setProgressFor] = useState(null); // {patient_id, patient, logs}

  const load = () => {
    axios.get(`${API}/health/profile`, headers()).then((r) => setHealth(r.data.health)).catch(() => setHealth(null));
    axios.get(`${API}/health/logs/mine`, headers()).then((r) => setLogs(r.data.logs)).catch(() => {});
    axios.get(`${API}/health/practitioners?kind=${kind}`, headers()).then((r) => setPractitioners(r.data.practitioners)).catch(() => {});
    axios.get(`${API}/health/appointments/mine`, headers()).then((r) => setMine(r.data.appointments)).catch(() => {});
    if (isPractitioner) axios.get(`${API}/health/appointments/incoming`, headers()).then((r) => setIncoming(r.data.appointments)).catch(() => {});
  };
  useEffect(load, [kind]);

  const addLog = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/health/logs`, {
        weight_kg: logForm.weight_kg ? Number(logForm.weight_kg) : null,
        sugar_mgdl: logForm.sugar_mgdl ? Number(logForm.sugar_mgdl) : null,
        meal_note: logForm.meal_note,
      }, headers());
      toast.success("Logged 💚");
      setLogForm({ weight_kg: "", sugar_mgdl: "", meal_note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const viewProgress = async (patientId) => {
    try {
      const { data } = await axios.get(`${API}/health/patients/${patientId}/logs`, headers());
      setProgressFor(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (health) {
      axios.get(`${API}/health/recommendations`, headers()).then((r) => setRecs(r.data)).catch(() => setRecs(null));
    } else {
      setRecs(null);
    }
  }, [health]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/health/profile`, { ...pForm, age: Number(pForm.age) }, headers());
      setHealth(data.health);
      toast.success("Health profile saved — FUNZI is building your plan 💚");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const book = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/health/appointments`, { practitioner_id: bookingFor.id, ...bForm }, headers());
      toast.success("Consultation requested 🩺");
      setBookingFor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status, diet_note = "") => {
    try {
      await axios.post(`${API}/health/appointments/${id}/status`, { status, diet_note }, headers());
      toast.success(`Appointment ${status}`);
      setNoteFor("");
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggle = (key, v) => setPForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const tabs = [
    { id: "plan", label: `🤖 ${t(lang, "hb_plan")}` },
    { id: "doctors", label: `🩺 ${t(lang, "hb_doctors")}` },
    { id: "visits", label: `📅 ${t(lang, "hb_visits")}` },
  ];

  return (
    <div className="space-y-5" data-testid="panel-healthbite">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`hb-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`rounded-full px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === tb.id ? "bg-gradient-to-r from-[#22C55E] to-[#06B6D4] text-[#09090B]" : "border border-white/10 text-slate-400 hover:text-white"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "plan" && (
        <>
          {health === undefined ? (
            <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
          ) : !health ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="hb-setup">
              <p className="flex items-center gap-2 font-display text-sm font-bold"><HeartPulse className="h-4 w-4 text-[#22C55E]" /> {t(lang, "hb_setup")}</p>
              <div className="mt-4 space-y-4">
                <input data-testid="hb-age" type="number" min="5" max="110" value={pForm.age} onChange={(e) => setPForm({ ...pForm, age: e.target.value })} placeholder="Your age" className={inputCls} />
                <div>
                  <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Conditions</p>
                  <ChipRow options={masters.health_conditions || []} values={pForm.conditions} onToggle={(v) => toggle("conditions", v)} testid="hb-cond" />
                </div>
                <div>
                  <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Allergies</p>
                  <ChipRow options={masters.allergies || []} values={pForm.allergies} onToggle={(v) => toggle("allergies", v)} testid="hb-allergy" />
                </div>
                <div>
                  <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Goal</p>
                  <ChipRow options={masters.health_goals || []} values={pForm.goal ? [pForm.goal] : []} onToggle={(v) => setPForm({ ...pForm, goal: v })} testid="hb-goal" />
                </div>
                <div>
                  <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Lifestyle</p>
                  <ChipRow options={masters.lifestyles || []} values={pForm.lifestyle ? [pForm.lifestyle] : []} onToggle={(v) => setPForm({ ...pForm, lifestyle: v })} testid="hb-life" />
                </div>
                <button data-testid="hb-save" onClick={saveProfile} disabled={busy || !pForm.age || !pForm.goal}
                  className="rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
                  {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : t(lang, "hb_save")}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-[#22C55E]/25 bg-[#22C55E]/[0.05] p-4" data-testid="hb-profile">
                <span className="text-lg">💚</span>
                <p className="font-jbmono text-[10px] text-slate-400">
                  {health.age}y · {health.goal} · {(health.conditions || []).join(", ") || "no conditions"} · ⚠️ {(health.allergies || []).join(", ") || "no allergies"}
                </p>
                <button data-testid="hb-edit" onClick={() => { setPForm({ age: String(health.age), conditions: health.conditions || [], allergies: health.allergies || [], goal: health.goal, lifestyle: health.lifestyle }); setHealth(null); setRecs(null); }}
                  className="ml-auto rounded-full border border-white/15 px-3.5 py-1.5 text-[10px] text-slate-300 hover:text-white rtl:ml-0 rtl:mr-auto">
                  Edit
                </button>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="hb-logs">
                <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-500">📝 {t(lang, "hl_log")}</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <input data-testid="hl-weight" type="number" step="0.1" value={logForm.weight_kg} onChange={(e) => setLogForm({ ...logForm, weight_kg: e.target.value })} placeholder={t(lang, "hl_weight")} className="w-32 rounded-xl border border-white/10 bg-[#09090B]/80 px-3 py-2.5 font-jbmono text-xs text-white outline-none focus:border-[#22C55E]/60" />
                  <input data-testid="hl-sugar" type="number" value={logForm.sugar_mgdl} onChange={(e) => setLogForm({ ...logForm, sugar_mgdl: e.target.value })} placeholder={t(lang, "hl_sugar")} className="w-36 rounded-xl border border-white/10 bg-[#09090B]/80 px-3 py-2.5 font-jbmono text-xs text-white outline-none focus:border-[#22C55E]/60" />
                  <input data-testid="hl-meal" value={logForm.meal_note} onChange={(e) => setLogForm({ ...logForm, meal_note: e.target.value })} placeholder={t(lang, "hl_meal")} className={`${inputCls} min-w-40 flex-1 !py-2.5`} />
                  <button data-testid="hl-save" onClick={addLog} disabled={busy || (!logForm.weight_kg && !logForm.sugar_mgdl && !logForm.meal_note)}
                    className="rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] px-5 py-2.5 font-display text-[10px] font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
                    {t(lang, "hl_save")}
                  </button>
                </div>
                {(logs.filter((l) => l.weight_kg != null).length >= 2 || logs.filter((l) => l.sugar_mgdl != null).length >= 2) && (
                  <div className="mt-4 space-y-3">
                    <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-600">{t(lang, "hl_trend")}</p>
                    <TrendChart logs={logs} metric="weight_kg" color="bg-gradient-to-t from-[#22C55E]/50 to-[#22C55E]" label={t(lang, "hl_weight")} unit="kg" />
                    <TrendChart logs={logs} metric="sugar_mgdl" color="bg-gradient-to-t from-[#7C3AED]/50 to-[#7C3AED]" label={t(lang, "hl_sugar")} unit="" />
                  </div>
                )}
              </div>
              {recs === null ? (
                <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
              ) : (
                <>
                  <div className="rounded-2xl border border-[#06B6D4]/30 bg-[#06B6D4]/[0.06] p-4" data-testid="hb-insight">
                    <p className="flex items-center gap-1.5 font-jbmono text-[9px] uppercase tracking-widest text-[#06B6D4]"><Sparkles className="h-3 w-3" /> FUNZI Health{recs.source === "fallback" ? " · basic mode" : ""}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-200">{recs.plan_note}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {recs.meals.map((m, i) => (
                      <div key={i} data-testid={`hb-meal-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                        <p className="text-sm font-bold">{m.name}</p>
                        {m.price > 0 && <p className="font-jbmono text-[10px] text-[#22C55E]">₹{m.price} · on FoodBite</p>}
                        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">{m.why}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {tab === "doctors" && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {[["", "All"], ["doctor", "🩺 Doctors"], ["nutritionist", "🥗 Nutritionists"]].map(([k, label]) => (
              <button key={k} data-testid={`hb-kind-${k || "all"}`} onClick={() => setKind(k)}
                className={`rounded-full px-4 py-2 text-[10px] font-bold ${kind === k ? "bg-[#22C55E] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                {label}
              </button>
            ))}
          </div>
          {practitioners.length === 0 ? (
            <p className="text-sm text-slate-500">No practitioners onboarded yet — invite your doctor to join FUNZ.</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {practitioners.map((p) => (
                <div key={p.id} data-testid={`hb-doc-${p.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#22C55E] to-[#06B6D4] text-lg">{p.avatar || (p.role === "doctor" ? "🩺" : "🥗")}</span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold">{p.name}</p>
                      <p className="font-jbmono text-[10px] text-slate-500">{p.specialization || p.role}{p.location ? ` · ${p.location}` : ""}</p>
                    </div>
                  </div>
                  {bookingFor?.id === p.id ? (
                    <div className="mt-3 space-y-2 rounded-xl border border-[#22C55E]/30 bg-[#22C55E]/[0.05] p-3">
                      <input data-testid="hb-book-date" type="date" value={bForm.date} onChange={(e) => setBForm({ ...bForm, date: e.target.value })} className={inputCls} />
                      <ChipRow options={masters.consult_slots || []} values={[bForm.slot]} onToggle={(v) => setBForm({ ...bForm, slot: v })} testid="hb-slot" />
                      <input data-testid="hb-book-reason" value={bForm.reason} onChange={(e) => setBForm({ ...bForm, reason: e.target.value })} placeholder="Reason (e.g. diabetes diet plan)" className={inputCls} />
                      <div className="flex gap-2">
                        <button data-testid="hb-book-confirm" onClick={book} disabled={busy || !bForm.date}
                          className="flex-1 rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] py-2.5 font-display text-xs font-bold uppercase text-[#09090B] disabled:opacity-50">
                          {busy ? <LoaderCircle className="mx-auto h-3.5 w-3.5 animate-spin" /> : t(lang, "hb_book")}
                        </button>
                        <button onClick={() => setBookingFor(null)} className="rounded-full border border-white/15 px-4 text-xs text-slate-400">{t(lang, "cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <button data-testid={`hb-book-${p.id.slice(-6)}`} onClick={() => setBookingFor(p)}
                      className="mt-3 w-full rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B]">
                      {t(lang, "hb_book")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "visits" && (
        <div className="space-y-4">
          {isPractitioner && incoming.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500"><Stethoscope className="h-3.5 w-3.5" /> {t(lang, "hb_requests")}</p>
              <div className="space-y-2.5">
                {incoming.map((a) => (
                  <div key={a.id} data-testid={`hb-req-${a.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-sm font-bold">{a.patient_name}</p>
                    <p className="font-jbmono text-[10px] text-slate-500">{a.date} · {a.slot}{a.reason ? ` · ${a.reason}` : ""}</p>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                        a.status === "confirmed" ? "bg-[#22C55E]/10 text-[#22C55E]" : a.status === "requested" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-white/5 text-slate-400"}`}>
                        {a.status}
                      </span>
                      {a.status === "requested" && (
                        <button data-testid={`hb-confirm-${a.id.slice(-6)}`} onClick={() => setStatus(a.id, "confirmed")}
                          className="rounded-full bg-[#22C55E] px-4 py-1.5 text-[10px] font-bold text-[#09090B]">Confirm</button>
                      )}
                      {a.status === "confirmed" && (
                        <button data-testid={`hb-complete-${a.id.slice(-6)}`} onClick={() => setNoteFor(a.id)}
                          className="rounded-full bg-[#06B6D4] px-4 py-1.5 text-[10px] font-bold text-[#09090B]">Complete + diet plan</button>
                      )}
                      {["requested", "confirmed"].includes(a.status) && (
                        <button data-testid={`hb-cancel-${a.id.slice(-6)}`} onClick={() => setStatus(a.id, "cancelled")}
                          className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] text-slate-400 hover:text-white">{t(lang, "cancel")}</button>
                      )}
                      <button data-testid={`hb-progress-${a.id.slice(-6)}`} onClick={() => viewProgress(a.patient_id)}
                        className="rounded-full border border-[#22C55E]/40 px-4 py-1.5 text-[10px] font-bold text-[#22C55E] hover:bg-[#22C55E]/10">
                        📈 {t(lang, "hl_progress")}
                      </button>
                    </div>
                    {progressFor && progressFor.patient && progressFor.patient.name === a.patient_name && (
                      <div className="mt-3 rounded-xl border border-white/10 bg-[#09090B]/70 p-3" data-testid={`hb-progress-panel-${a.id.slice(-6)}`}>
                        <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">
                          {progressFor.patient.name} · {progressFor.patient.health?.goal || "—"} · ⚠️ {(progressFor.patient.health?.allergies || []).join(", ") || "none"}
                        </p>
                        {progressFor.logs.length === 0 ? (
                          <p className="mt-2 text-xs text-slate-500">No logs yet from this patient.</p>
                        ) : (
                          <div className="mt-2 space-y-2">
                            <TrendChart logs={progressFor.logs} metric="weight_kg" color="bg-gradient-to-t from-[#22C55E]/50 to-[#22C55E]" label={t(lang, "hl_weight")} unit="kg" />
                            <TrendChart logs={progressFor.logs} metric="sugar_mgdl" color="bg-gradient-to-t from-[#7C3AED]/50 to-[#7C3AED]" label={t(lang, "hl_sugar")} unit="" />
                            <p className="font-jbmono text-[9px] text-slate-600">Latest meal: {progressFor.logs[progressFor.logs.length - 1]?.meal_note || "—"}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {noteFor === a.id && (
                      <div className="mt-3 flex gap-2">
                        <input data-testid="hb-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Diet plan / prescription notes…" className={inputCls} />
                        <button data-testid="hb-note-save" onClick={() => setStatus(a.id, "completed", note)}
                          className="shrink-0 rounded-full bg-[#22C55E] px-4 text-[10px] font-bold text-[#09090B]">Done</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2.5">
            {mine.length === 0 && !isPractitioner && <p className="text-sm text-slate-500">No consultations yet — book one from Doctors.</p>}
            {mine.map((a) => (
              <div key={a.id} data-testid={`hb-visit-${a.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{a.practitioner_name} <span className="font-jbmono text-[10px] text-slate-500">· {a.practitioner_role}</span></p>
                    <p className="font-jbmono text-[10px] text-slate-500">{a.date} · {a.slot}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                    a.status === "completed" ? "bg-[#22C55E]/10 text-[#22C55E]" : a.status === "requested" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-white/5 text-slate-400"}`}>
                    {a.status}
                  </span>
                </div>
                {a.diet_note && (
                  <p className="mt-2 rounded-xl border border-[#22C55E]/20 bg-[#22C55E]/[0.05] px-3.5 py-2.5 text-xs leading-relaxed text-slate-200" data-testid={`hb-diet-${a.id.slice(-6)}`}>
                    📋 {a.diet_note}
                  </p>
                )}
                {a.status === "requested" && (
                  <button data-testid={`hb-mycancel-${a.id.slice(-6)}`} onClick={() => setStatus(a.id, "cancelled")}
                    className="mt-2.5 rounded-full border border-white/15 px-4 py-1.5 text-[10px] text-slate-400 hover:text-white">{t(lang, "cancel")}</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
